import * as React from 'react';
import { createContext, useContext, useRef, useState } from 'react';

import type { AiCoach_RespondToUserMessageInput as AiCoachRespondToUserMessageInput } from '__generated__/graphql-types';

import type { ChatMessageProps } from 'bundles/ai-coach-platform/components/patterns/chat/common';

type PageChatType = 'fullscreen' | 'side-by-side' | null;
type CoachChatConfig = Omit<AiCoachRespondToUserMessageInput, 'message'> & { message?: undefined };

type GlobalCoachChatStateProviderType = {
  isContextProvided: boolean;

  coachOpen: boolean;
  setCoachOpen: React.Dispatch<React.SetStateAction<boolean>>;

  contextConfig: CoachChatConfig;
  setContextConfig: React.Dispatch<React.SetStateAction<CoachChatConfig>>;
  isContextConfigLoading: boolean;
  setIsContextConfigLoading: React.Dispatch<React.SetStateAction<boolean>>;

  renderCustomMessage?: (props: ChatMessageProps) => React.ReactElement | null;
  setRenderCustomMessage: React.Dispatch<
    React.SetStateAction<((props: ChatMessageProps) => React.ReactElement | null) | undefined>
  >;

  triggerElementRef: React.MutableRefObject<HTMLElement | null>;
  setTriggerElementRef: (elm: HTMLElement | null) => void;

  pageChatType: PageChatType;
  setPageChatType: React.Dispatch<React.SetStateAction<PageChatType>>;
};

const defaultGlobalCoachChatState: GlobalCoachChatStateProviderType = {
  isContextProvided: false,
  coachOpen: false,
  setCoachOpen: () => {},
  contextConfig: {
    config: {
      coachActionId: '',
      coachModeId: 'BASIC',
    },
  },
  setContextConfig: () => {},
  isContextConfigLoading: false,
  setIsContextConfigLoading: () => {},
  renderCustomMessage: undefined,
  setRenderCustomMessage: () => {},
  triggerElementRef: { current: null },
  setTriggerElementRef: () => {},
  pageChatType: null,
  setPageChatType: () => {},
};

const GlobalCoachChatStateContext = createContext(defaultGlobalCoachChatState);

export const useGlobalCoachChatState = () => {
  const context = useContext(GlobalCoachChatStateContext);
  return context;
};

export const GlobalCoachChatStateProvider = ({ children }: { children: React.ReactNode }) => {
  const [coachOpen, setCoachOpen] = useState(defaultGlobalCoachChatState.coachOpen);
  const [contextConfig, setContextConfig] = useState(defaultGlobalCoachChatState.contextConfig);
  const [isContextConfigLoading, setIsContextConfigLoading] = useState(
    defaultGlobalCoachChatState.isContextConfigLoading
  );
  const [pageChatType, setPageChatType] = useState(defaultGlobalCoachChatState.pageChatType);
  const [renderCustomMessage, setRenderCustomMessage] = useState<
    ((props: ChatMessageProps) => React.ReactElement | null) | undefined
  >(defaultGlobalCoachChatState.renderCustomMessage);
  const triggerElementRef = useRef<HTMLElement | null>(null);
  const setTriggerElementRef = (elm: HTMLElement | null) => {
    triggerElementRef.current = elm;
  };

  return (
    <GlobalCoachChatStateContext.Provider
      value={{
        isContextProvided: true,
        coachOpen,
        setCoachOpen,
        contextConfig,
        setContextConfig: (...data) => {
          setContextConfig(...data);
        },
        isContextConfigLoading,
        setIsContextConfigLoading,
        renderCustomMessage,
        setRenderCustomMessage,
        triggerElementRef,
        setTriggerElementRef,
        pageChatType,
        setPageChatType,
      }}
    >
      {children}
    </GlobalCoachChatStateContext.Provider>
  );
};
